<template>
  <div class="sp-checkbox" :class="classModifiers">
    <div class="sp-checkbox__control">
      <span class="sp-checkbox__input-wrapper" @click="toggleChecked">
        <input
          ref="checkboxInput"
          type="checkbox"
          class="sp-checkbox__input"
          :checked="checked"
          :indeterminate.prop="indeterminate"
          :value="value"
          :disabled="disabled"
          :aria-labelledby="labelledBy"
          :aria-label="attrs.ariaLabel"
          :aria-describedby="describedBy"
          :aria-required="ariaRequired"
          :aria-invalid="ariaInvalid"
          @click.stop
          @input.stop="onInnerInput"
          @change="onInnerChange"
          @focus="onFocus"
          @blur="onBlur"
        />
        <span class="sp-checkbox__box" aria-hidden="true">
          <svg class="sp-checkbox__glyph" viewBox="0 0 16 16" focusable="false">
            <path class="sp-checkbox__check" d="M3.5 8.25 6.5 11.25 12.5 4.75" />
            <path class="sp-checkbox__dash" d="M4 8 12 8" />
          </svg>
        </span>
      </span>

      <sp-label :id="LABEL_ID" class="sp-checkbox__label" @click="toggleChecked">
        <slot ref="labelSlot" name="label">{{ label }}</slot>
      </sp-label>
    </div>

    <div v-if="shownMessages.length > 0" :id="MESSAGES_ID" class="sp-checkbox__messages">
      <span v-for="(message, index) in shownMessages" :key="index" class="sp-checkbox__message">{{ message }}</span>
    </div>
    <div v-if="showError" :id="ERROR_ID" class="sp-checkbox__error">{{ errorText }}</div>
  </div>
</template>

<script setup>
import { useEventListener } from "@vueuse/core";
import { computed, nextTick, ref, toRef, useAttrs, useTemplateRef, watch } from "vue";

import { useCheckedModel } from "#ce/composables/useCheckedModel";
import { useExpose } from "#ce/composables/useExpose";
import { useForm } from "#ce/composables/useForm";
import { useFormControl } from "#ce/composables/useFormControl";
import { useHostEmit } from "#ce/composables/useHostEmit";
import { formAssociatedHost } from "#ce/framework/utils";
import { crossedInteractiveContent } from "#ce/utils/events";
import { t } from "#ce/shared/forms-i18n";
import { hasSlotContent } from "#ce/utils/slots";
import { isPresent, toArray, toBoolean } from "#ce/utils/props";
import { props as checkboxProps } from "./props.js";

// sp-checkbox is a genuine form control: submit a value, match :disabled, respond to <fieldset disabled>.
defineOptions(formAssociatedHost({ inheritAttrs: false }));

const emit = defineEmits(["input", "update-checked"]);
const { emitBubbling } = useHostEmit();

const props = defineProps(checkboxProps);

// Element ids resolve within the shadow root, so fixed values cannot collide across instances.
const LABEL_ID = "checkbox-label";
const MESSAGES_ID = "checkbox-messages";
const ERROR_ID = "checkbox-error";

const checkboxInput = useTemplateRef("checkboxInput");

const attrs = useAttrs();
const { checked, defaultChecked, setChecked, reset } = useCheckedModel();
const indeterminate = ref(isPresent(attrs.indeterminate));

// Any resolution of the checked state leaves the mixed state behind.
function clearIndeterminate() {
  indeterminate.value = false;
}

// Sync so a listener of the toggle's own events reads the resolved mixed state, not the one the
// click replaced.
watch(checked, clearIndeterminate, { flush: "sync" });
watch(
  () => attrs.indeterminate,
  (value) => (indeterminate.value = isPresent(value)),
);

const { exposeProperties } = useExpose();

exposeProperties({
  type: {
    configurable: true,
    get: () => "checkbox",
  },
  checked: {
    configurable: true,
    get: () => checked.value,
    set: setChecked,
  },
  defaultChecked: {
    configurable: true,
    get: () => defaultChecked.value,
  },
  indeterminate: {
    configurable: true,
    get: () => indeterminate.value,
    set: (value) => (indeterminate.value = isPresent(value)),
  },
});

// Disabled is governed by the form-control composable: presence-based own
// attribute plus ancestor <fieldset disabled>, not a standalone toBoolean(prop).
const { disabled } = useFormControl({ disabled: toRef(props, "disabled") });

// The box and the label sit outside the native input's click handling. Guard
// those clicks, then route through the input so input:checked remains the
// visual source of truth.
function toggleChecked(event) {
  if (disabled.value || crossedInteractiveContent(event, event.currentTarget)) {
    return;
  }

  // Focused first, the way a native `<label for>` activation does, so anything keyed to focus —
  // the hint — reacts to a label click as it does to the control's own.
  checkboxInput.value?.focus();
  checkboxInput.value?.click();
}

// The inner input's own `input` is stopped and re-reported after the model is set, so
// `host.checked` is already current for any listener the event reaches. A v-model binding reads
// it inside the host's `change`, which does not compose out of the shadow root — the host
// dispatches its own. This is the only path that emits `update-checked`: a native control
// reports user input alone, and property writes, attribute syncs, and resets mirror silently.
function onInnerInput(event) {
  setChecked(event.target.checked);
  emitBubbling("input", checked.value);
  emit("update-checked", checked.value);
}

function onInnerChange() {
  emitChange();
}

const isFocused = ref(false);

function onFocus() {
  isFocused.value = true;
}

function onBlur() {
  isFocused.value = false;
}

// A message displaces the hint, which otherwise shows only while the box has focus. An empty
// `messages` says nothing, so the hint still gets its turn. `errorMessages` renders separately,
// below both.
const shownMessages = computed(() => {
  const messages = toArray(props.messages ?? [])
    .filter(Boolean)
    .map(String);

  if (messages.length > 0) {
    return messages;
  }

  if (props.hint && (toBoolean(props.persistentHint) || isFocused.value)) {
    return [props.hint];
  }

  return [];
});

const errorText = computed(() =>
  toArray(props.errorMessages ?? [])
    .filter(Boolean)
    .join(" "),
);
const showError = computed(() => Boolean(errorText.value));
const ariaInvalid = computed(() => (showError.value ? "true" : undefined));
const isRequired = computed(() => isPresent(props.required));
const ariaRequired = computed(() => (isRequired.value ? "true" : undefined));

// An aria-labelledby pointing at an empty label element names the input with an
// empty string, and the input sits in the shadow root where no outside markup
// can name it. Point at the label only while it holds text.
const labelSlot = useTemplateRef("labelSlot");
const hasSlottedLabel = ref(false);

watch(labelSlot, updateHasSlottedLabel);
useEventListener(labelSlot, "slotchange", updateHasSlottedLabel);

function updateHasSlottedLabel() {
  hasSlottedLabel.value = Boolean(labelSlot.value) && hasSlotContent(labelSlot);
}

const labelledBy = computed(() => (props.label?.trim() || hasSlottedLabel.value ? LABEL_ID : undefined));

const describedBy = computed(() => {
  const ids = [];

  if (shownMessages.value.length > 0) {
    ids.push(MESSAGES_ID);
  }
  if (showError.value) {
    ids.push(ERROR_ID);
  }

  return ids.length > 0 ? ids.join(" ") : undefined;
});

const classModifiers = computed(() => ({
  "--checked": checked.value,
  "--indeterminate": indeterminate.value,
  "--disabled": disabled.value,
  "--error": showError.value,
  "--label-prepend": toBoolean(props.labelPrepend),
}));

// Form association: an unchecked box submits nothing, matching a native checkbox.
// `change` is dispatched from the toggle handler, where the user acts.
const { emitChange, mirrorFormValue, mirrorValueMissing, onFormReset } = useForm();
const formValue = computed(() => (checked.value ? props.value : null));

mirrorFormValue(formValue);

// A required checkbox is satisfied only while it is checked, so the mixed state
// does not clear valueMissing any more than an unchecked box does.
const valueMissing = computed(() => isRequired.value && !checked.value);

// Read through the i18n instance rather than off a resolved promise, so the
// message follows the bundle arriving with a client's own wording.
const validationMessage = computed(() => props.requiredMessage || t("checkbox_value_missing"));

mirrorValueMissing(valueMissing, validationMessage, checkboxInput);

// A reset restores the state the attributes describe, the way a native checkbox
// returns to its `checked` content attribute. The mixed state is restored after
// the checked change flushes, since resolving checked clears it.
onFormReset(async () => {
  reset();
  await nextTick();
  indeterminate.value = isPresent(attrs.indeterminate);
});
</script>

<style>
:host {
  display: block;
}
</style>

<style lang="scss" scoped>
.sp-checkbox {
  --box-size: var(--sp-comp-checkbox-size, var(--sp-ref-scale-18));
  --box-border-width: var(--sp-comp-checkbox-border-width, var(--sp-ref-scale-2));
  --box-border-radius: var(--sp-comp-checkbox-border-radius, var(--sp-sys-shape-control));
  --box-border-color: var(--sp-comp-checkbox-border-color, var(--sp-sys-color-outline));
  --box-border-color-checked: var(--sp-comp-checkbox-border-color-checked, var(--sp-sys-color-primary));
  --box-bg-color: var(--sp-comp-checkbox-background-color, transparent);
  --box-bg-color-checked: var(--sp-comp-checkbox-background-color-checked, var(--sp-sys-color-primary));
  --glyph-color: var(--sp-comp-checkbox-glyph-color, var(--sp-sys-color-on-primary));

  display: grid;
  gap: var(--sp-comp-checkbox-row-gap, var(--sp-ref-scale-4));

  &__control {
    display: var(--sp-comp-checkbox-control-display, grid);
    grid-template-columns: auto 1fr;
    grid-template-areas: "control label";
    align-items: start;
    gap: var(--sp-comp-checkbox-gap, var(--sp-ref-scale-8));
  }

  &__input-wrapper {
    grid-area: control;
    position: relative;
    display: var(--sp-comp-checkbox-input-display, flex);
    contain: var(--sp-comp-checkbox-input-contain, none);
    width: var(--box-size);
    height: var(--box-size);
    // Centre the box on the label's first line rather than on the whole label,
    // the way a native checkbox sits beside wrapping text. The font shorthand
    // matches what sp-label renders the text with, so `1lh` here measures the
    // label's own line box.
    font: var(--sp-comp-checkbox-label-font, var(--sp-sys-form-field-label-short));
    margin-block-start: var(--sp-comp-checkbox-offset-block, max(0px, calc((1lh - var(--box-size)) * 0.5)));
  }

  &__input {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    margin: 0;
    padding: 0;
    opacity: 0;
    cursor: pointer;
    z-index: 1;

    &:disabled {
      cursor: not-allowed;
    }

    &:focus-visible + .sp-checkbox__box {
      outline: var(--sp-comp-checkbox-focus-outline-width, var(--sp-sys-state-focus-indicator-thickness)) solid
        var(--sp-comp-checkbox-focus-outline-color, var(--sp-sys-color-primary));
      outline-offset: var(--sp-comp-checkbox-focus-outline-offset, var(--sp-sys-state-focus-indicator-outer-offset));
    }
  }

  &__box {
    box-sizing: border-box;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    border: var(--box-border-width) solid var(--box-border-color);
    border-radius: var(--box-border-radius);
    background-color: var(--box-bg-color);
    transition:
      background-color 0.15s ease-in-out,
      border-color 0.15s ease-in-out;
  }

  &__glyph {
    width: 100%;
    height: 100%;
    fill: none;
    stroke: var(--glyph-color);
    stroke-width: var(--sp-comp-checkbox-glyph-stroke-width, 2);
    stroke-linecap: round;
    stroke-linejoin: round;
  }

  &__check,
  &__dash {
    opacity: 0;
  }

  &__label {
    grid-area: label;
    display: var(--sp-comp-checkbox-label-display, inline);
    vertical-align: var(--sp-comp-checkbox-label-align, baseline);
    margin-inline-start: var(--sp-comp-checkbox-label-margin-inline, 0);
  }

  &__messages {
    display: flex;
    flex-direction: column;
    gap: var(--sp-comp-checkbox-message-gap, var(--sp-sys-form-field-message-gap));
    color: var(--sp-comp-checkbox-message-color, var(--sp-sys-form-field-message-color));
    font-size: var(--sp-comp-checkbox-message-font-size, var(--sp-sys-form-field-message-font-size));
    line-height: var(--sp-comp-checkbox-message-line-height, var(--sp-sys-form-field-message-line-height));
  }

  &__error {
    color: var(--sp-comp-checkbox-error-color, var(--sp-sys-color-error));
    font-size: var(--sp-comp-checkbox-error-font-size, var(--sp-ref-font-size-semi-medium));
  }

  &.--label-prepend &__control {
    grid-template-columns: 1fr auto;
    grid-template-areas: "label control";
  }

  &.--checked:not(.--indeterminate) &__check,
  &.--indeterminate &__dash {
    opacity: 1;
  }

  &.--checked &__box,
  &.--indeterminate &__box {
    border-color: var(--box-border-color-checked);
    background-color: var(--box-bg-color-checked);
  }

  &.--error &__box {
    border-color: var(--sp-comp-checkbox-border-color-error, var(--sp-sys-color-error));
  }

  &.--disabled &__box,
  &.--disabled &__label {
    opacity: var(--sp-comp-checkbox-disabled-opacity, 0.4);
  }

  &.--disabled &__label {
    --sp-comp-label-cursor: not-allowed;
  }
}
</style>
